"use client";

import { Check } from "lucide-react";
import { signOut } from "next-auth/react";
import { cn } from "~/lib/utils";
import { Link } from "~/components/Link";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuGroup,
    DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
    Organization,
    OrganizationScope,
    PlanTypes,
    User,
} from "~/modules/user/store/types";
import { getPlanLabel } from "~/modules/user/util/getPlanLabel";
import { api } from "~/utils/api";
import { Routes } from "../../routes";
import { NavigationUserEvents } from "~/modules/analytics/events/navigation";
import { track } from "~/modules/analytics/track";
import { resetAnalyticsIdentity } from "~/modules/analytics/reset";
import * as Analytics from "~/modules/auth/analytics";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { useFeatureFlag } from "~/modules/feature_flag/hooks";
import { useUser } from "~/modules/user/hooks/useUser";
import { useDispatch } from "react-redux";
import { togglePreviewFeatures } from "~/modules/user/store";
import { toggleDebugPreviewFeatures } from "~/modules/debug/store";
import { useDebug } from "~/modules/debug/hooks/use-debug";

// // // //

const getOrganizationDisplayName = (org: Organization, userEmail: string) => {
    const orgName = org.name;

    const singleUserOrg = org.scope === OrganizationScope.SINGLEUSER;
    if (singleUserOrg) {
        // return `${userEmail} (Personal)`;
        return `${userEmail}`;
        // return `Personal Space`;
    }

    if (!orgName || orgName.trim() === "") {
        return `${userEmail}`;
    }

    return orgName;
};

// Lower values sort first in the Teams list
const PLAN_TYPE_SORT_ORDER: Record<PlanTypes, number> = {
    [PlanTypes.PREMIUM_ALL_ACCESS]: 0,
    [PlanTypes.TRIAL_ALL_ACCESS]: 1,
    [PlanTypes.PREMIUM_ACADEMIC]: 2,
    [PlanTypes.PREMIUM_DATA_DOWNLOADS]: 3,
    [PlanTypes.TRIAL_DATA_DOWNLOADS]: 4,
    [PlanTypes.PREMIUM_STARTER]: 5,
    [PlanTypes.TRIAL_STARTER]: 6,
    [PlanTypes.FREE]: 7,
};

const userMenuItemClassName =
    "cursor-pointer hover:bg-slate-100 hover:text-slate-800 focus:bg-slate-100 focus:text-slate-800 data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-800";

// Admin-only button to toggle preview features on/off
function TogglePreviewFeatures() {
    const dispatch = useDispatch();

    const showExpFeatures = useFeatureFlag(
        FeatureNames.enableExperimentalFeatures,
    );

    return (
        <DropdownMenuItem
            onClick={() => dispatch(togglePreviewFeatures())}
            className={cn(
                "flex gap-2 items-center whitespace-nowrap justify-between",
                userMenuItemClassName,
            )}
        >
            Preview Features
            {showExpFeatures ? (
                <span className="bg-green-500/10 text-green-600 p-1 rounded-md text-xs">
                    ON
                </span>
            ) : (
                <span className="bg-red-500/10 text-red-600 p-1 rounded-md text-xs">
                    OFF
                </span>
            )}
        </DropdownMenuItem>
    );
}

// Admin-only button to toggle feature preview debug mode on/off
function TogglePreviewFeaturesDebug() {
    const { debug_preview_features } = useDebug();
    const dispatch = useDispatch();

    return (
        <DropdownMenuItem
            onClick={() => dispatch(toggleDebugPreviewFeatures())}
            className={cn(
                "flex gap-2 items-center justify-between",
                userMenuItemClassName,
            )}
        >
            Debug
            {debug_preview_features ? (
                <span className="bg-green-500/10 text-green-600 p-1 rounded-md text-xs">
                    ON
                </span>
            ) : (
                <span className="bg-red-500/10 text-red-600 p-1 rounded-md text-xs">
                    OFF
                </span>
            )}
        </DropdownMenuItem>
    );
}

// Admin-only buttons to toggle feature preview and debug mode
function AdminMenuSection() {
    const { isAdmin } = useUser();

    // Only show this for superadmins
    if (!isAdmin) {
        return null;
    }

    return (
        <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
                <DropdownMenuLabel className="text-xs text-foreground/60">
                    Admin Only: Release Mgmt.
                </DropdownMenuLabel>
                <TogglePreviewFeatures />
                <TogglePreviewFeaturesDebug />
            </DropdownMenuGroup>
        </>
    );
}

export function UserMenu(props: { user: User }) {
    const { user } = props;
    const updateDefaultOrganizationID =
        api.users.updateDefaultOrganizationID.useMutation();

    // Get all organizations from memberships, sorted by plan tier
    const organizations = user.memberships
        .map((membership) => membership.organization)
        .sort(
            (a, b) =>
                PLAN_TYPE_SORT_ORDER[a.plan.type] -
                PLAN_TYPE_SORT_ORDER[b.plan.type],
        );

    // Current active organization
    const activeOrganization = user.organization;

    // Check if user is owner of the active organization
    const currentMembership = user.memberships.find(
        (m) => m.organizationID === activeOrganization.id,
    );
    const isOwnerOfSingleTeam =
        organizations.length === 1 && currentMembership?.role === "OWNER";

    const handleOrganizationSwitch = (organizationId: string) => {
        // Don't switch if clicking on the already-active organization
        if (organizationId === activeOrganization.id) {
            return;
        }

        // Update the default organization via tRPC mutation
        updateDefaultOrganizationID
            .mutateAsync({ organizationID: organizationId })
            .then(() => {
                // Reload the page to refetch user state with the updated organization
                window.location.reload();
            })
            .catch((error) => {
                console.error("Failed to switch organization:", error);
            });
    };

    // Don't render if there's no active organization or no memberships
    if (!activeOrganization || organizations.length === 0) {
        return null;
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger className="group flex cursor-pointer rounded-full bg-card text-sm focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2">
                <span className="sr-only">Open user menu</span>
                <div className="relative">
                    <span
                        className={cn(
                            "h-8 w-8 rounded-full bg-primary text-primary-foreground font-light flex items-center justify-center transition-all",
                            {
                                "group-hover:ring-2 group-hover:ring-blue-400/60 group-hover:ring-offset-1 group-focus-visible:ring-2 group-focus-visible:ring-blue-400/60 group-focus-visible:ring-offset-1":
                                    activeOrganization.plan.type ===
                                    PlanTypes.FREE,
                            },
                        )}
                    >
                        {user.email[0].toUpperCase()}
                    </span>
                    {activeOrganization.plan.type === PlanTypes.FREE && (
                        <span className="absolute -bottom-1 right-2 bg-blue-100 text-blue-600 text-[8px] font-semibold px-1 rounded leading-tight">
                            FREE
                        </span>
                    )}
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className="min-w-56 rounded-lg z-500"
                align="end"
                sideOffset={4}
            >
                {/* User Email */}
                <div className="px-2 py-1.5">
                    <p className="text-sm text-muted-foreground truncate">
                        {user.email}
                    </p>
                </div>
                <DropdownMenuSeparator />

                {/* Teams Section - only show if multiple orgs or not owner of single team */}
                {!isOwnerOfSingleTeam && (
                    <>
                        <DropdownMenuLabel className="text-muted-foreground text-xs">
                            Teams
                        </DropdownMenuLabel>
                        {organizations.map((org) => {
                            const isActive = org.id === activeOrganization.id;
                            return (
                                <DropdownMenuItem
                                    key={org.id}
                                    className={cn(
                                        "gap-2 p-2",
                                        userMenuItemClassName,
                                    )}
                                    onClick={() =>
                                        handleOrganizationSwitch(org.id)
                                    }
                                >
                                    <div className="flex size-6 items-center justify-center rounded-md bg-slate-900 text-white">
                                        <span className="text-xs font-semibold">
                                            {(org.name ||
                                                user.email)[0]?.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-sm font-medium">
                                            {getOrganizationDisplayName(
                                                org,
                                                user.email,
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {getPlanLabel(org.plan.type)}
                                        </div>
                                    </div>
                                    {isActive && (
                                        <Check className="size-4 text-primary" />
                                    )}
                                </DropdownMenuItem>
                            );
                        })}
                        <DropdownMenuSeparator />
                    </>
                )}

                {/* Profile Link */}
                <DropdownMenuItem asChild className={userMenuItemClassName}>
                    <Link
                        href={Routes.settingsProfile}
                        onClick={() => {
                            track(NavigationUserEvents.internalLinkClick, {
                                href: Routes.settingsProfile,
                                label: "User Menu - Profile Link",
                            });
                        }}
                    >
                        Profile
                    </Link>
                </DropdownMenuItem>

                {/* Team Link */}
                <DropdownMenuItem asChild className={userMenuItemClassName}>
                    <Link
                        href={Routes.settingsOrganization}
                        onClick={() => {
                            track(NavigationUserEvents.internalLinkClick, {
                                href: Routes.settingsOrganization,
                                label: "User Menu - Team Link",
                            });
                        }}
                    >
                        Team
                    </Link>
                </DropdownMenuItem>

                {/* Sign Out */}
                <DropdownMenuItem
                    onClick={() => {
                        // NOTE - order matters: capture the logout event while the
                        // user is still identified, then clear the identity so the
                        // next visitor on this device starts a new person profile.
                        Analytics.trackLogout();
                        resetAnalyticsIdentity();
                        signOut();
                    }}
                    className={userMenuItemClassName}
                >
                    Sign out
                </DropdownMenuItem>

                {/* Current Plan */}
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                    <p className="text-xs text-muted-foreground">
                        Plan: {getPlanLabel(activeOrganization.plan.type)}
                    </p>
                </div>

                {/* Admin-only section */}
                <AdminMenuSection />
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
